import { useEffect, useState } from "react";
import { Box, Button, Container, Typography, Card, Stack, Divider, Grid, Avatar } from "@mui/material";
import { useHistory } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGripHorizontal, faLayerGroup, faList, faBoxOpen, faPlus, faBoxes, faCheckCircle, faFolderOpen, faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";

// Componentes QUD
import CustomBreadcrumbs from "../../../components/breadcrumbs";
import QUDProgress from "../../../components/progress";
import ProductsDataTable from "./table";
import { changeProductStatus, getProducts } from "../../../controllers/company/products/ProductsMerchantController";

export default function Products() {
    const [products, setProducts] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    
    // Estados simulados para las estadísticas (deberás calcularlos o traerlos del backend)
    const [stats, setStats] = useState({
        total: 0,
        active: 0,
        categories: 0, // Aquí puedes poner el total real de categorías
        outOfStock: 0  // O productos inactivos
    });

    const history = useHistory();

    useEffect(() => {
        window.scrollTo(0, 0);
        handleGetProducts();
    }, []);

    const redirect = (route) => {
        history.push(route);
    }

    const handleGetProducts = async () => {
        setIsLoading(true);
        const response = await getProducts();
        if (response.success === true) {
            const data = response.data.products || [];
            setProducts(data);
            
            // Calculamos las estadísticas básicas basadas en los productos recibidos
            // (Asumiendo que 'status' u otro campo te dice si está activo)
            const activeCount = data.filter(p => p.status === 'active' || p.estatus === 1).length;
            
            setStats({
                total: data.length,
                active: activeCount,
                categories: 5, // Sustituir por el dato real de tu backend
                outOfStock: 0  // Sustituir por lógica de stock si la tienes
            });
        }
        setIsLoading(false);
    }

    const handleOnChangeStatus = async (product_id) => {
        const response = await changeProductStatus(product_id);
        if (response.success === true) {
            handleGetProducts();
        }
    }

    // Estilo unificado para las tarjetas (Limpio y moderno)
    const cardStyle = {
        elevation: 0,
        sx: { border: '1px solid #e0e0e0', borderRadius: 3, p: 2.5, bgcolor: '#ffffff' }
    };

    return (
        <Container maxWidth='xl' sx={{ py: 4 }}>
            <QUDProgress open={isLoading} />

            <CustomBreadcrumbs returnTo='/' crumbs={[{ name: 'Productos', link: '/products' }]} />
            
            {/* HEADER PRINCIPAL */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, mb: 4 }}>
                <Box>
                    <Typography variant="h5" fontWeight={700} sx={{ color: '#222' }}>
                        Tus Productos
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
                        Gestiona el inventario, categorías y extras de tu negocio.
                    </Typography>
                </Box>
                
                <Button
                    variant='contained'
                    color="primary"
                    startIcon={<FontAwesomeIcon icon={faPlus} />}
                    onClick={() => redirect('products/create')}
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3, py: 1 }}
                >
                    Nuevo Producto
                </Button>
            </Box>

            {/* === NUEVA CABECERA DE ESTADÍSTICAS (KPIs) === */}
            <Grid container spacing={3} mb={4}>
                {/* KPI 1: Total Productos */}
                <Grid item xs={12} sm={6} md={3}>
                    <Card {...cardStyle}>
                        <Stack direction="row" spacing={2} alignItems="center">
                            <Avatar variant='rounded' sx={{ width: 44, height: 44, bgcolor: '#f0f4f8', color: '#1976d2', borderRadius: 2 }}>
                                <FontAwesomeIcon icon={faBoxes} size="sm" />
                            </Avatar>
                            <Box flexGrow={1}>
                                <Typography variant="caption" sx={{ color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                    Total Productos
                                </Typography>
                                <Typography variant="h5" sx={{ fontWeight: 700, color: '#222', my: 0.2 }}>
                                    {stats.total}
                                </Typography>
                            </Box>
                        </Stack>
                    </Card>
                </Grid>

                {/* KPI 2: Productos Activos */}
                <Grid item xs={12} sm={6} md={3}>
                    <Card {...cardStyle}>
                        <Stack direction="row" spacing={2} alignItems="center">
                            <Avatar variant='rounded' sx={{ width: 44, height: 44, bgcolor: '#e8f5e9', color: '#2e7d32', borderRadius: 2 }}>
                                <FontAwesomeIcon icon={faCheckCircle} size="sm" />
                            </Avatar>
                            <Box flexGrow={1}>
                                <Typography variant="caption" sx={{ color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                    Activos Públicos
                                </Typography>
                                <Typography variant="h5" sx={{ fontWeight: 700, color: '#222', my: 0.2 }}>
                                    {stats.active}
                                </Typography>
                            </Box>
                        </Stack>
                    </Card>
                </Grid>

                {/* KPI 3: Categorías */}
                <Grid item xs={12} sm={6} md={3}>
                    <Card {...cardStyle}>
                        <Stack direction="row" spacing={2} alignItems="center">
                            <Avatar variant='rounded' sx={{ width: 44, height: 44, bgcolor: '#f3e5f5', color: '#9c27b0', borderRadius: 2 }}>
                                <FontAwesomeIcon icon={faFolderOpen} size="sm" />
                            </Avatar>
                            <Box flexGrow={1}>
                                <Typography variant="caption" sx={{ color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                    Categorías
                                </Typography>
                                <Typography variant="h5" sx={{ fontWeight: 700, color: '#222', my: 0.2 }}>
                                    {stats.categories}
                                </Typography>
                            </Box>
                        </Stack>
                    </Card>
                </Grid>

                {/* KPI 4: Alertas (Agotados / Ocultos) */}
                <Grid item xs={12} sm={6} md={3}>
                    <Card {...cardStyle}>
                        <Stack direction="row" spacing={2} alignItems="center">
                            <Avatar variant='rounded' sx={{ width: 44, height: 44, bgcolor: '#fff3e0', color: '#ed6c02', borderRadius: 2 }}>
                                <FontAwesomeIcon icon={faExclamationTriangle} size="sm" />
                            </Avatar>
                            <Box flexGrow={1}>
                                <Typography variant="caption" sx={{ color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                    Agotados / Ocultos
                                </Typography>
                                <Typography variant="h5" sx={{ fontWeight: 700, color: '#222', my: 0.2 }}>
                                    {stats.total - stats.active}
                                </Typography>
                            </Box>
                        </Stack>
                    </Card>
                </Grid>
            </Grid>

            {/* SUB-NAVEGACIÓN DE CONFIGURACIÓN */}
            <Stack direction="row" spacing={1.5} mb={3} sx={{ overflowX: 'auto', pb: 1 }}>
                <Button
                    variant='outlined'
                    startIcon={<FontAwesomeIcon icon={faGripHorizontal} style={{ opacity: 0.6 }} />}
                    onClick={() => redirect('products/categories')}
                    sx={{ 
                        borderRadius: 2, textTransform: 'none', color: '#444', borderColor: '#e0e0e0', bgcolor: '#fff',
                        fontWeight: 600, px: 2,
                        '&:hover': { borderColor: 'primary.main', bgcolor: 'primary.50', color: 'primary.main', '& .svg-inline--fa': { opacity: 1 } }
                    }}
                >
                    Categorías
                </Button>

                <Button
                    variant='outlined'
                    startIcon={<FontAwesomeIcon icon={faList} style={{ opacity: 0.6 }} />}
                    onClick={() => redirect('products/attributes')}
                    sx={{ 
                        borderRadius: 2, textTransform: 'none', color: '#444', borderColor: '#e0e0e0', bgcolor: '#fff',
                        fontWeight: 600, px: 2,
                        '&:hover': { borderColor: 'primary.main', bgcolor: 'primary.50', color: 'primary.main', '& .svg-inline--fa': { opacity: 1 } }
                    }}
                >
                    Atributos
                </Button>

                <Button
                    variant='outlined'
                    startIcon={<FontAwesomeIcon icon={faLayerGroup} style={{ opacity: 0.6 }} />}
                    onClick={() => redirect('products/modifiergroups')}
                    sx={{ 
                        borderRadius: 2, textTransform: 'none', color: '#444', borderColor: '#e0e0e0', bgcolor: '#fff',
                        fontWeight: 600, px: 2,
                        '&:hover': { borderColor: 'primary.main', bgcolor: 'primary.50', color: 'primary.main', '& .svg-inline--fa': { opacity: 1 } }
                    }}
                >
                    Modificadores / Extras
                </Button>
            </Stack>

            {/* CONTENEDOR DE LA TABLA */}
            
                {products.length !== 0 ? (
                    <ProductsDataTable products={products} onChangeStatus={handleOnChangeStatus} />
                ) : (
                    !isLoading && (
                        /* EMPTY STATE */
                        <Box sx={{ py: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                            <Box sx={{ width: 80, height: 80, bgcolor: '#f8f9fa', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
                                <FontAwesomeIcon icon={faBoxOpen} size="3x" color="#ccc" />
                            </Box>
                            <Typography variant="h6" fontWeight={700} sx={{ color: '#333' }} gutterBottom>
                                Aún no tienes productos registrados
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#666', maxWidth: 400, mb: 4 }}>
                                Comienza agregando tu primer producto para que tus clientes puedan empezar a comprar o reservar.
                            </Typography>
                            <Button
                                variant='outlined'
                                color="primary"
                                startIcon={<FontAwesomeIcon icon={faPlus} />}
                                onClick={() => redirect('products/create')}
                                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                            >
                                Crear mi primer producto
                            </Button>
                        </Box>
                    )
                )}
        </Container>
    );
}